// Byråns tjänstekatalog i kontot (ADR 0064). Byrån ändrar alltid ett utkast.
// Finns bara en publicerad version skapas utkastet vid första sparandet, som
// en kopia av den publicerade. Publicera gör utkastet till den version som
// offerterna räknas på.
import { serviceCatalogSchema } from '@leadledger/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiFel, data } from '../api/client.js';
import { nycklar, usePrismodeller } from '../api/queries.js';
import { useAuth } from '../auth/auth.js';
import type { components } from '../api/schema.js';
import type { ServiceCatalog } from './sammanfattning.js';

export type Prismodell = components['schemas']['PricingModel'];
export type PrismodellSammanfattning = components['schemas']['PricingModelSummary'];

/** Ett fel när katalogen sparas, med fältet i katalogen och ett meddelande. */
export interface Katalogfel {
  path: string;
  message: string;
}

export class OgiltigKatalog extends Error {
  constructor(readonly fel: Katalogfel[]) {
    super('Katalogen är inte giltig');
  }
}

const modellNyckel = (id: string | undefined) => ['prismodell', id] as const;

/** Kontrollerar katalogen som API:t gör, så att felen syns innan något skickas. */
export function kontrollera(katalog: ServiceCatalog): Katalogfel[] {
  const resultat = serviceCatalogSchema.safeParse(katalog);
  if (resultat.success) return [];
  return resultat.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message }));
}

/** Antal tjänster och inställningar som skiljer utkastet från den publicerade versionen. */
export function antalAndringar(utkast: ServiceCatalog, publicerad: ServiceCatalog): number {
  const lika = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const idn = new Set([...utkast.services, ...publicerad.services].map((s) => s.id));
  let n = 0;
  for (const id of idn) {
    const a = utkast.services.find((s) => s.id === id);
    const b = publicerad.services.find((s) => s.id === id);
    if (!lika(a, b)) n++;
  }
  const ovrigt = (k: ServiceCatalog) => ({ ...k, services: undefined });
  if (!lika(ovrigt(utkast), ovrigt(publicerad))) n++;
  return n;
}

export function useKatalog() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const modeller = usePrismodeller();
  const lista = modeller.data ?? [];
  const utkast = lista.find((m) => m.status === 'draft');
  const publicerad = lista.find((m) => m.status === 'published');
  const aktuellId = utkast?.id ?? publicerad?.id;

  const hamta = (id: string | undefined) => async () =>
    data(await api.GET('/v1/admin/pricing-models/{id}', { params: { path: { id: id ?? '' } } }));

  const aktuell = useQuery({
    queryKey: modellNyckel(aktuellId),
    queryFn: hamta(aktuellId),
    enabled: aktuellId !== undefined,
  });
  // Den publicerade versionen behövs för att räkna ändringarna i utkastet.
  const jamforId = utkast && publicerad ? publicerad.id : undefined;
  const jamfor = useQuery({
    queryKey: modellNyckel(jamforId),
    queryFn: hamta(jamforId),
    enabled: jamforId !== undefined,
  });

  const uppdatera = async (modell: Prismodell) => {
    queryClient.setQueryData(modellNyckel(modell.id), modell);
    await queryClient.invalidateQueries({ queryKey: nycklar.prismodeller });
  };

  const spara = useMutation({
    mutationFn: async (katalog: ServiceCatalog) => {
      const fel = kontrollera(katalog);
      if (fel.length > 0) throw new OgiltigKatalog(fel);
      let id = utkast?.id;
      if (!id) {
        if (!publicerad) throw new Error('Det finns ingen katalog att ändra');
        id = data(
          await api.POST('/v1/admin/pricing-models', { body: { copyOf: publicerad.id } }),
        ).id;
      }
      const svar = await api.PUT('/v1/admin/pricing-models/{id}', {
        params: { path: { id } },
        body: { definition: katalog },
      });
      if (svar.response.status === 400 && svar.error && 'errors' in svar.error) {
        throw new OgiltigKatalog(
          svar.error.errors.map((e) => ({
            path: e.path.replace(/^definition\./, ''),
            message: e.message,
          })),
        );
      }
      return data(svar);
    },
    onSuccess: uppdatera,
  });

  const publicera = useMutation({
    mutationFn: async () => {
      if (!utkast) throw new Error('Det finns inget utkast att publicera');
      return data(
        await api.POST('/v1/admin/pricing-models/{id}/publish', {
          params: { path: { id: utkast.id } },
        }),
      );
    },
    onSuccess: uppdatera,
  });

  const andringar =
    utkast && aktuell.data && jamfor.data
      ? antalAndringar(aktuell.data.definition, jamfor.data.definition)
      : null;

  return {
    laddar: modeller.isPending || (aktuellId !== undefined && aktuell.isPending),
    fel: modeller.isError || aktuell.isError,
    lista,
    utkast,
    publicerad,
    katalog: aktuell.data?.definition,
    /** Antal ändringar mot den publicerade versionen. null när det inte går att jämföra. */
    andringar,
    spara,
    publicera,
  };
}

export type Katalog = ReturnType<typeof useKatalog>;

/** Meddelandet för ett fel när katalogen sparas eller publiceras. */
export function felText(err: unknown): string {
  if (err instanceof OgiltigKatalog) return 'Katalogen har fel. Rätta dem och spara igen.';
  if (err instanceof ApiFel && err.status === 403) {
    // Förhandsvisningen har en egen text (ADR 0106).
    const detail = (err.problem as { detail?: unknown } | undefined)?.detail;
    return typeof detail === 'string' && detail.startsWith('Kontot är en förhandsvisning')
      ? detail
      : 'Bara byråns ägare kan ändra katalogen.';
  }
  if (err instanceof ApiFel && err.status === 409) {
    return 'Katalogen har ändrats av någon annan. Ladda om sidan och försök igen.';
  }
  return 'Det gick inte att spara. Försök igen om en stund.';
}
